import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { formatStorefrontMoneyFromShop } from "../../../utils/format-storefront-money";
import { isPreviewPlaceholderProduct } from "../../../utils/dummyProductGenerator";
import styles from "./PostPurchaseOrderSummary.module.css";

const PREVIEW_PRICE_PLACEHOLDER = "XXX.XX";

function parseAmount(raw) {
  if (raw == null || raw === "") return { kind: "empty" };
  const asNumber = Number(raw);
  if (Number.isFinite(asNumber)) return { kind: "number", value: asNumber };
  const asText = String(raw).trim();
  if (asText) return { kind: "text", value: asText };
  return { kind: "empty" };
}

function formatMoney(amount, shop) {
  const parsed = parseAmount(amount);
  if (parsed.kind === "text") return parsed.value;
  if (parsed.kind !== "number") return PREVIEW_PRICE_PLACEHOLDER;
  const formatted = formatStorefrontMoneyFromShop(parsed.value, shop);
  if (!formatted || /nan/i.test(String(formatted))) {
    return PREVIEW_PRICE_PLACEHOLDER;
  }
  return formatted;
}

/**
 * Prefer schema product; when admin preview pads dummies only in local widget
 * state, fall back to the first section product or a placeholder line.
 */
function resolveSummaryProduct(productSection, productProp) {
  if (productProp) return productProp;
  const list = Array.isArray(productSection?.products)
    ? productSection.products
    : [];
  return list.find((p) => p && (p.id != null || p.title)) || null;
}

export default function PostPurchaseOrderSummary({
  productSection,
  shop,
  product: productProp,
}) {
  const product = resolveSummaryProduct(productSection, productProp);

  const pricing = useMemo(() => {
    const rawPrice =
      product?.price ??
      product?.variants?.[0]?.price ??
      PREVIEW_PRICE_PLACEHOLDER;
    const parsed = parseAmount(rawPrice);
    const isPlaceholder =
      !product ||
      isPreviewPlaceholderProduct(product) ||
      parsed.kind !== "number";

    if (isPlaceholder) {
      return {
        isPlaceholder: true,
        baseLabel: PREVIEW_PRICE_PLACEHOLDER,
        totalLabel: PREVIEW_PRICE_PLACEHOLDER,
        discountLabel: PREVIEW_PRICE_PLACEHOLDER,
      };
    }

    const base = parsed.value;
    const discountValue = Number(
      product?.discount_value ?? productSection?.discount_value ?? 0
    );
    const discountType =
      product?.discount_type ||
      productSection?.discount_type ||
      "percentage";
    let total = base;
    if (discountValue > 0) {
      total =
        discountType === "percentage"
          ? base - (base * discountValue) / 100
          : base - discountValue;
    }
    total = Math.max(0, total);
    const discount = Math.max(0, base - total);
    return {
      isPlaceholder: false,
      baseLabel: formatMoney(base, shop),
      totalLabel: formatMoney(total, shop),
      discountLabel: formatMoney(discount, shop),
    };
  }, [product, productSection, shop]);

  // Always show summary in the post-purchase offer (including dummy preview).
  return (
    <div className={styles.wrap}>
      <div className={styles.row}>
        <span>Subtotal</span>
        <span>{pricing.totalLabel}</span>
      </div>
      <div className={styles.row}>
        <span>Discount</span>
        <span>{pricing.discountLabel}</span>
      </div>
      <div className={styles.row}>
        <span>Shipping</span>
        <span className={styles.muted}>Free</span>
      </div>
      <div className={`${styles.row} ${styles.totalRow}`}>
        <span>Total</span>
        <strong>{pricing.totalLabel}</strong>
      </div>
      <p className={styles.taxNote}>Including $0.00 in taxes</p>
    </div>
  );
}

PostPurchaseOrderSummary.propTypes = {
  productSection: PropTypes.object,
  shop: PropTypes.object,
  product: PropTypes.object,
};
